import Vue from "vue";
import { getStoreBuilder, BareActionContext } from "vuex-typex";
import { AxiosError } from "axios";
import authAxios from "@/state/auth-axios"; // Use this to make okta-auth'd axios requests
import rootStore from "@/state/store";

// === STATE - Prepare initial state, store builder
export const initialPartnersState: IPartnersStore = {
	partners: [],
	partnersToken: "",
	userPartner: null
};

// Prepare store builder for use throughout
const storeBuilder = getStoreBuilder<IRootStore>();
const moduleBuilder = storeBuilder.module<IPartnersStore>(
	"partners",
	initialPartnersState,
);

// === GETTERS - Like computed data or used to get a part of the data or return it in a specific way and cache it

// === MUTATIONS - Synchronous work that mutates the data store. Should be quick
const SET_PARTNERS_MUTATION = (
	state: IPartnersStore,
	data: IPartnerList,
) => {
	Vue.set(state, "partners", data); // For reactivity
};
const SET_PARTNERS_TOKEN_MUTATION = (
	state: IPartnersStore,
	data: IPartnerList,
) => {
	Vue.set(state, "partnersToken", data); // For reactivity
};
const SET_PARTNER_MUTATION = (
	state: IPartnersStore,
	data: IPartner,
) => {
	Vue.set(state, data.id, data); // For reactivity
};
const SET_USER_PARTNER_MUTATION = (
	state: IPartnersStore,
	data: IPartner,
) => {
	Vue.set(state, "userPartner", data); // For reactivity
};

// === ACTIONS / SERVICES - Asynchronous work that gets data, changes the data for storage, and handles errors.
const GET_PARTNERS_ACTION = (
	{}: BareActionContext<IPartnersStore, IRootStore>,
	params: IFetchPartnersArgs
): Promise<IPartnerList> => {
	return fetchPartners(params)
		.then((data: IPartnerList) => {
			partnersStore.commitSetPartners(data);
			return data;
		});
};
const GET_PARTNER_ACTION = (
	{}: BareActionContext<IPartnersStore, IRootStore>,
	id: string
): Promise<IPartner> => {
	return fetchPartner(id)
		.then((data: IPartner) => {
			partnersStore.commitSetPartner(data);
			return data;
		});
};
const ADD_PARTNER_ACTION = (
	{}: BareActionContext<IPartnersStore, IRootStore>,
	data: IPartner
): Promise<IPartner> => {
	const config = rootStore.state.config as ISiteConfig;
	const url = `${config.partnersUrl}`;
	const params = {
		team: data.team
	};

	return authAxios.post(url, data, {params})
		.then((response) => {
			const dataFromServer: { data: IPartner } = response; // remove headers
			return dataFromServer.data;
		})
		.catch((err: AxiosError) => {
			throw err;
		});
};
const UPDATE_PARTNER_ACTION = (
	{}: BareActionContext<IPartnersStore, IRootStore>,
	data: IPartner
): Promise<IPartner> => {
	const config = rootStore.state.config as ISiteConfig;
	const url = `${config.partnersUrl}`;

	return authAxios.put(url, data)
		.then((response) => {
			const dataFromServer: { data: IPartner } = response.data; // remove headers
			return dataFromServer.data;
		})
		.catch((err: AxiosError) => {
			throw err;
		});
};
const DELETE_PARTNER_ACTION = (
	{}: BareActionContext<IPartnersStore, IRootStore>,
	id: string
): Promise<any> => {
	const config = rootStore.state.config as ISiteConfig;
	const url = `${config.partnersUrl}/${id}`;

	return authAxios.delete(url)
		.then((response) => {
			const dataFromServer: { data: any } = response; // remove headers
			return dataFromServer.data;
		})
		.catch((err: AxiosError) => {
			throw err;
		});
};

// === SERVICES
interface IFetchPartnersArgs {
	params: any;
}
const phoneNumberFormat = function(phoneNumber: string | number | undefined) {
	if (phoneNumber) {
		if (typeof phoneNumber === "string") {
			phoneNumber = phoneNumber.replace(/[^\d]/, "");
		} else {
			phoneNumber = phoneNumber.toString();
		}
		phoneNumber = phoneNumber.replace(/[^\d]/, "");
		const length = phoneNumber.length;
		if (phoneNumber.length >= 10) {
			phoneNumber = phoneNumber.substring(length - 10, length - 7)
				+ "-" + phoneNumber.substring(length - 7, length - 4)
				+ "-" + phoneNumber.substring(length - 4);
		}
	}
	return phoneNumber;
}
const fetchPartners = (args: IFetchPartnersArgs): Promise<IPartnerList> => {
	const {
		params,
	} = args;

	// prepare url for request
	const config = rootStore.state.config as ISiteConfig;
	const url = `${config.partnersUrl}`;

	return authAxios.get(url, {
			params,
		})
		.then((response) => {
			const data = response.data; // remove headers

			var partners: IPartnerList = [];
			if (params.continuationToken) {
				partners = partnersStore.state.partners;
			}
			// Transform partners
			if (data.listResults) {
				data.listResults.forEach((entry: IPartner) => {
					entry.phoneNumber = phoneNumberFormat(entry.phoneNumber);
					if (entry.settings) {
						entry.settings.questionsPhoneNumber = phoneNumberFormat(entry.settings.questionsPhoneNumber);
					}
					partners.push(entry);
					partnersStore.commitSetPartner(entry);
				});
			}
			partnersStore.commitSetPartnersToken(data.continuationToken);

			return partners;
		})
		.catch((err: AxiosError) => {
			throw err;
		});
};
const fetchPartner = (id: string): Promise<IPartner> => {
	const config = rootStore.state.config as ISiteConfig;
	const url = `${config.partnersUrl}/${id}`;
	const params = {
	};

	return authAxios.get(url, {params})
		.then((response) => {
			const data: any = response.data; // remove headers

			data.phoneNumber = phoneNumberFormat(data.phoneNumber);
			if (data.settings) {
				data.settings.questionsPhoneNumber = phoneNumberFormat(data.settings.questionsPhoneNumber);
			}

			// Update data for display
			return data;
		})
		.catch((err: AxiosError) => {
			throw err;
		});
};

// === EXPORTS - Modules state or storage handling. Gets combined with the root store on compile or run.
// Prepare this module's state for export
const stateGetter = moduleBuilder.state();

// exported module interface
const partnersStore = {
	// state
	get state() { return stateGetter(); },

	// getters

	// mutations
	commitSetPartners: moduleBuilder.commit(SET_PARTNERS_MUTATION, "SET_PARTNERS"),
	commitSetPartnersToken: moduleBuilder.commit(SET_PARTNERS_TOKEN_MUTATION, "SET_PARTNERS_TOKEN"),
	commitSetPartner: moduleBuilder.commit(SET_PARTNER_MUTATION, "SET_PARTNER"),
	commitSetUserPartner: moduleBuilder.commit(SET_USER_PARTNER_MUTATION, "SET_USER_PARTNER"),

	// action
	dispatchGetPartners: moduleBuilder.dispatch(
		GET_PARTNERS_ACTION,
		"getPartners",
	),
	dispatchGetPartner: moduleBuilder.dispatch(
		GET_PARTNER_ACTION,
		"getPartner",
	),
	dispatchAddPartners: moduleBuilder.dispatch(
		ADD_PARTNER_ACTION,
		"addPartner",
	),
	dispatchUpdatePartners: moduleBuilder.dispatch(
		UPDATE_PARTNER_ACTION,
		"updatePartner",
	),
	dispatchDeletePartners: moduleBuilder.dispatch(
		DELETE_PARTNER_ACTION,
		"deletePartner",
	),
};

// Export default store object
export default partnersStore;
